import React,{useState,useEffect} from 'react';
import Layout from '../components/layout/Layout';
import {useNavigate,useLocation} from 'react-router-dom';
import api from '../services/api';

const GENDERS=['ALL','MALE','FEMALE','CHILD','NEONATE'];

export default function LabReferenceRangesPage(){
  const nav=useNavigate();
  const loc=useLocation();
  const preselect=(loc.state as any)?.parameterId||'';
  const [params,setParams]=useState<any[]>([]);
  const [selectedId,setSelectedId]=useState(preselect);
  const [ranges,setRanges]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const [search,setSearch]=useState('');
  const [cat,setCat]=useState('');
  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  const [form,setForm]=useState({gender:'ALL',ageMin:'',ageMax:'',normalMin:'',normalMax:'',criticalLow:'',criticalHigh:'',unit:'',notes:''});
  const [editId,setEditId]=useState<string|null>(null);
  const defaultForm={gender:'ALL',ageMin:'',ageMax:'',normalMin:'',normalMax:'',criticalLow:'',criticalHigh:'',unit:'',notes:''};

  useEffect(()=>{
    api.getLabParameters({}).then(r=>setParams(r.data||[])).catch(()=>{});
  },[]);

  useEffect(()=>{
    if(!selectedId)return;
    setLoading(true);
    api.getLabParameters({parameterId:selectedId}).then(r=>{
      const p=r.data?.find?.((x:any)=>x.id===selectedId)||r.data?.[0];
      if(p){setRanges(p.reference_ranges||p.referenceRanges||[]);setForm(f=>({...f,unit:p.unit||f.unit}));}
    }).catch(()=>{}).finally(()=>setLoading(false));
    api.getReferenceRanges(selectedId).then(r=>setRanges(r.data||[])).catch(()=>{});
  },[selectedId]);

  const selectedParam=params.find((p:any)=>p.id===selectedId);
  const cats=Array.from(new Set(params.map((p:any)=>p.category||p.group||''))).filter(Boolean);
  const filtered=params.filter((p:any)=>{
    const name=p.parameter_name||p.parameterName||p.displayName||'';
    const matchSearch=!search||name.toLowerCase().includes(search.toLowerCase());
    const matchCat=!cat||(p.category||p.group||'')===(cat);
    return matchSearch&&matchCat;
  });

  const saveRange=async()=>{
    if(!selectedId){notify('Select a parameter first');return;}
    if(!form.normalMin||!form.normalMax){notify('Normal range min/max are required');return;}
    setSaving(true);
    try{
      if(editId){
        await api.updateReferenceRange(editId,{...form,parameterId:selectedId});
        notify('✅ Range updated');
      } else {
        await api.addReferenceRange({...form,parameterId:selectedId});
        notify('✅ Range added');
      }
      setEditId(null);setForm({...defaultForm,unit:selectedParam?.unit||''});
      api.getReferenceRanges(selectedId).then(r=>setRanges(r.data||[])).catch(()=>{});
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    setSaving(false);
  };

  const deleteRange=async(id:string)=>{
    if(!window.confirm('Delete this reference range?'))return;
    try{await api.deleteReferenceRange(id);setRanges(r=>r.filter((x:any)=>x.id!==id));notify('✅ Deleted');}catch{}
  };

  return(
    <Layout title="Lab Reference Ranges" actions={
      <button onClick={()=>nav('/lab/parameter-master')} className="btn-secondary text-sm">← Back to Parameter Master</button>
    }>
      {msg&&<div className="fixed top-4 right-4 bg-white border shadow-xl rounded-xl px-4 py-3 text-sm z-50">{msg}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-160px)]">

        {/* LEFT: Parameter list */}
        <div className="card overflow-hidden flex flex-col">
          <div className="p-3 border-b bg-slate-50">
            <input className="input w-full text-sm" placeholder="Search parameters..." value={search} onChange={e=>setSearch(e.target.value)}/>
            <select className="input w-full text-xs mt-2" value={cat} onChange={e=>setCat(e.target.value)}>
              <option value="">All Categories</option>
              {cats.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex-1 overflow-y-auto divide-y">
            {filtered.map((p:any)=>{
              const name=p.parameter_name||p.parameterName||p.displayName;
              const code=p.parameter_code||p.parameterCode||'';
              const rangeCount=p.reference_ranges?.length||0;
              const isSelected=selectedId===p.id;
              return(
                <button key={p.id} onClick={()=>setSelectedId(p.id)}
                  className={`w-full text-left p-3 hover:bg-blue-50 transition-colors ${isSelected?'bg-blue-50 border-r-2 border-[#015CA6]':''}`}>
                  <p className={`text-sm font-medium ${isSelected?'text-[#02357A]':'text-slate-800'}`}>{name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-slate-400 font-mono">{code}</span>
                    {p.unit&&<span className="text-xs text-slate-400">· {p.unit}</span>}
                    <span className={`ml-auto text-xs px-1.5 py-0.5 rounded ${rangeCount>0?'bg-green-100 text-green-700':'bg-slate-100 text-slate-400'}`}>
                      {rangeCount} range{rangeCount!==1?'s':''}
                    </span>
                  </div>
                </button>
              );
            })}
            {filtered.length===0&&<p className="p-4 text-center text-slate-400 text-sm">No parameters found</p>}
          </div>
        </div>

        {/* RIGHT: Ranges for selected parameter */}
        <div className="col-span-2 flex flex-col gap-4 overflow-y-auto">
          {!selectedId?(
            <div className="card p-8 text-center text-slate-400">
              <p className="text-4xl mb-3">📐</p>
              <p className="font-medium">Select a parameter from the left to manage its reference ranges</p>
            </div>
          ):(
            <>
              {/* Parameter header */}
              <div className="card p-4 bg-blue-50 border-blue-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="font-bold text-blue-800 text-lg">{selectedParam?.parameter_name||selectedParam?.parameterName||selectedParam?.displayName}</h2>
                    <div className="flex gap-3 mt-1 text-sm text-[#02357A]">
                      <span>Code: <b>{selectedParam?.parameter_code||selectedParam?.parameterCode}</b></span>
                      <span>Unit: <b>{selectedParam?.unit||'—'}</b></span>
                      <span>Category: <b>{selectedParam?.category||'—'}</b></span>
                      <span>Type: <b>{selectedParam?.data_type||selectedParam?.dataType||'NUMERIC'}</b></span>
                    </div>
                  </div>
                  <span className="bg-blue-100 text-[#02357A] px-2 py-1 rounded text-xs font-semibold">{ranges.length} ranges</span>
                </div>
              </div>

              {/* Add/Edit range form */}
              <div className="card p-4">
                <h3 className="font-semibold text-slate-700 mb-3">{editId?'✏️ Edit Reference Range':'➕ Add Reference Range'}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div><label className="label">Gender</label>
                    <select className="input" value={form.gender} onChange={e=>setForm({...form,gender:e.target.value})}>
                      {GENDERS.map(g=><option key={g}>{g}</option>)}
                    </select>
                  </div>
                  <div><label className="label">Age Min (years)</label><input className="input" type="number" min="0" value={form.ageMin} onChange={e=>setForm({...form,ageMin:e.target.value})} placeholder="0"/></div>
                  <div><label className="label">Age Max (years)</label><input className="input" type="number" min="0" value={form.ageMax} onChange={e=>setForm({...form,ageMax:e.target.value})} placeholder="999"/></div>
                  <div><label className="label">Normal Min *</label><input className="input font-mono" type="number" step="any" value={form.normalMin} onChange={e=>setForm({...form,normalMin:e.target.value})} placeholder="e.g. 13.0"/></div>
                  <div><label className="label">Normal Max *</label><input className="input font-mono" type="number" step="any" value={form.normalMax} onChange={e=>setForm({...form,normalMax:e.target.value})} placeholder="e.g. 17.0"/></div>
                  <div><label className="label">Unit</label><input className="input" value={form.unit} onChange={e=>setForm({...form,unit:e.target.value})} placeholder="g/dL, mg/dL..."/></div>
                  <div><label className="label text-red-600">Critical Low ⚠️</label><input className="input border-red-200 font-mono" type="number" step="any" value={form.criticalLow} onChange={e=>setForm({...form,criticalLow:e.target.value})} placeholder="e.g. 7.0"/></div>
                  <div><label className="label text-red-600">Critical High ⚠️</label><input className="input border-red-200 font-mono" type="number" step="any" value={form.criticalHigh} onChange={e=>setForm({...form,criticalHigh:e.target.value})} placeholder="e.g. 20.0"/></div>
                  <div><label className="label">Notes</label><input className="input" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="e.g. Adult Male"/></div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={saveRange} disabled={saving} className="btn-primary">{saving?'Saving...':(editId?'Update Range':'Add Range')}</button>
                  {editId&&<button onClick={()=>{setEditId(null);setForm({...defaultForm,unit:selectedParam?.unit||''});}} className="btn-secondary">Cancel Edit</button>}
                </div>
              </div>

              {/* Ranges list */}
              <div className="card overflow-hidden">
                <div className="p-3 bg-slate-50 border-b">
                  <h3 className="font-semibold text-slate-700">📋 Reference Ranges ({ranges.length})</h3>
                </div>
                {loading?<p className="p-4 text-center text-slate-400">Loading...</p>
                :ranges.length===0?<p className="p-6 text-center text-slate-400">No reference ranges defined. Add one above.</p>
                :(
                  <div className="overflow-x-auto"><table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                      <tr>
                        <th className="p-3 text-left">Gender</th>
                        <th className="p-3 text-left">Age Group</th>
                        <th className="p-3 text-center">Normal Range</th>
                        <th className="p-3 text-center">Critical Range</th>
                        <th className="p-3 text-left">Unit</th>
                        <th className="p-3 text-left">Notes</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {ranges.map((r:any)=>(
                        <tr key={r.id} className="hover:bg-slate-50">
                          <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${r.gender==='MALE'?'bg-blue-100 text-[#02357A]':r.gender==='FEMALE'?'bg-pink-100 text-pink-700':r.gender==='CHILD'?'bg-green-100 text-green-700':'bg-slate-100 text-slate-600'}`}>{r.gender||'ALL'}</span></td>
                          <td className="p-3 text-xs">{r.age_min!=null||r.ageMin!=null?`${r.age_min||r.ageMin||0}–${r.age_max||r.ageMax||'∞'} yr`:'Any age'}</td>
                          <td className="p-3 text-center font-mono font-semibold text-green-700">{r.normal_min||r.normalMin} – {r.normal_max||r.normalMax}</td>
                          <td className="p-3 text-center font-mono text-red-600 text-xs">{(r.critical_low||r.criticalLow)?`< ${r.critical_low||r.criticalLow}`:''} {(r.critical_high||r.criticalHigh)?`> ${r.critical_high||r.criticalHigh}`:''}{!(r.critical_low||r.criticalLow)&&!(r.critical_high||r.criticalHigh)?'—':''}</td>
                          <td className="p-3 text-slate-500 text-xs">{r.unit||selectedParam?.unit||'—'}</td>
                          <td className="p-3 text-xs text-slate-400">{r.notes||'—'}</td>
                          <td className="p-3">
                            <div className="flex gap-1">
                              <button onClick={()=>{setEditId(r.id);setForm({gender:r.gender||'ALL',ageMin:String(r.age_min||r.ageMin||''),ageMax:String(r.age_max||r.ageMax||''),normalMin:String(r.normal_min||r.normalMin||''),normalMax:String(r.normal_max||r.normalMax||''),criticalLow:String(r.critical_low||r.criticalLow||''),criticalHigh:String(r.critical_high||r.criticalHigh||''),unit:r.unit||selectedParam?.unit||'',notes:r.notes||''});}} className="text-xs text-[#02357A] hover:underline">Edit</button>
                              <button onClick={()=>deleteRange(r.id)} className="text-xs text-red-500 hover:underline">Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
